/**
 * Strip JSON objects/arrays out of a text blob so they don't get rendered in
 * the chat. Used for AI response content where the model (or a tool surface)
 * occasionally emits raw JSON alongside prose, e.g.:
 *   `{"message":"Successfully updated report."}Updated the report...`
 *
 * The content is later rendered as markdown, so the walker is careful to leave
 * markdown intact:
 *   - Code spans/fences (`` `...` ``, ```` ```...``` ````) are copied verbatim,
 *     so JSON shown as an example is never touched.
 *   - A `{`/`[` candidate is only stripped when it actually parses as JSON.
 *     `[link]` from `[link](url)` isn't valid JSON, so links survive.
 *   - Arrays are only stripped when they carry structured data (strings or
 *     nested objects/arrays), so citations like `[1]` / `[2]` are preserved.
 *
 * Balanced brackets are matched with respect to quoted strings, so nested
 * objects don't get cut in half.
 */
export function stripJsonBlocks(text: string): string {
  if (!text) return text;

  const out: string[] = [];
  let i = 0;
  const n = text.length;

  while (i < n) {
    const ch = text[i];

    // Preserve code spans/blocks verbatim (incl. any JSON shown as an example).
    if (ch === '`') {
      const end = findCodeSpanEnd(text, i);
      out.push(text.slice(i, end));
      i = end;
      continue;
    }

    if (ch === '{' || ch === '[') {
      const end = findJsonEnd(text, i);
      if (end > 0 && isStrippableJson(text.slice(i, end))) {
        i = end;
        continue;
      }
    }

    out.push(ch);
    i++;
  }

  return out.join('').trim();
}

/**
 * Returns the index just past a markdown code span/fence that opens at `start`
 * (which must point at a backtick). Matches a closing run of the same length;
 * if there's no closing run, the rest of the string is treated as code.
 */
function findCodeSpanEnd(s: string, start: number): number {
  let openLen = 0;
  while (start + openLen < s.length && s[start + openLen] === '`') openLen++;
  const fence = '`'.repeat(openLen);
  const close = s.indexOf(fence, start + openLen);
  if (close === -1) return s.length;
  return close + openLen;
}

/**
 * A candidate `{...}`/`[...]` substring is strippable only when it parses as
 * JSON. Objects are always treated as stray blobs; arrays are kept unless they
 * contain structured data, so numeric citations like `[1]` survive.
 */
function isStrippableJson(candidate: string): boolean {
  let parsed: unknown;
  try {
    parsed = JSON.parse(candidate);
  } catch {
    return false;
  }
  if (Array.isArray(parsed)) {
    return parsed.some(
      (el) => typeof el === 'string' || (el !== null && typeof el === 'object'),
    );
  }
  return parsed !== null && typeof parsed === 'object';
}

function findJsonEnd(s: string, start: number): number {
  const open = s[start];
  if (open !== '{' && open !== '[') return -1;
  let depth = 0;
  let inString = false;
  let escape = false;
  for (let i = start; i < s.length; i++) {
    const c = s[i];
    if (escape) {
      escape = false;
      continue;
    }
    if (inString) {
      if (c === '\\') escape = true;
      else if (c === '"') inString = false;
      continue;
    }
    if (c === '"') {
      inString = true;
      continue;
    }
    if (c === '{' || c === '[') depth++;
    else if (c === '}' || c === ']') {
      depth--;
      if (depth === 0) return i + 1;
      if (depth < 0) return -1;
    }
  }
  return -1;
}
